import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from './components/Navigation';
import SEO from './components/SEO';
import ToolQuiz from './components/ToolQuiz';

export default function EcommercePage() {
  const [openFaq, setOpenFaq] = React.useState<number | null>(null);

  return (
    <>
      <SEO
        title="Ecommerce Data Warehouse: Shopify, Amazon & TikTok"
        description="Combine Shopify, Amazon, and TikTok Shop data. Reconcile orders, refunds, and fees in an ecommerce warehouse with reporting help from Greyfield Data."
        type="website"
        url="https://www.greyfielddata.com/ecommerce"
        keywords={['ecommerce data warehouse', 'Amazon analytics', 'Shopify analytics', 'TikTok Shop', 'Fivetran', 'Airbyte', 'Snowflake', 'BigQuery', 'Tableau', 'Power BI']}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Section - Dark with gradient background */}
        <section className="relative pt-32 pb-24 md:pt-44 md:pb-36 overflow-hidden bg-[#0a1628]">
          {/* Animated gradient background */}
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-blue-500/15 via-blue-600/10 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-blue-500/10 via-blue-600/5 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '10s' }} />
          </div>

          {/* Grid pattern overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />

          <div className="relative mx-auto max-w-7xl px-6">
            <div className="mb-16 max-w-4xl">
              <p className="mb-5 text-sm font-semibold uppercase tracking-[0.18em] text-blue-300">Ecommerce data systems</p>
              <h1 className="mb-8 text-5xl font-semibold leading-[1.08] tracking-tight text-white md:text-7xl">
                Ecommerce data warehouse
              </h1>
              <p className="max-w-3xl text-xl leading-relaxed text-slate-300 md:text-2xl">
                If you open Shopify, Amazon, Google Ads, and Meta to check yesterday's numbers, a warehouse can bring those reports together.
              </p>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <a href="mailto:linus@greyfielddata.com?subject=Ecommerce Data Warehouse Inquiry" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-7 py-4 font-semibold text-[#1a4e94] transition hover:bg-slate-100">
                  Review your reporting problem <span aria-hidden="true">→</span>
                </a>
                <Link to="/case-studies/dtc-brand-portfolio" className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-4 font-semibold text-white transition hover:bg-white/10">
                  See ecommerce work
                </Link>
              </div>
            </div>

            {/* Data sources: self-hosted simple-icons marks, wrapping row */}
            <div className="relative mt-16">
              <p className="mb-6 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Data sources</p>
              <ul className="mx-auto flex max-w-4xl flex-wrap items-center justify-center gap-x-8 gap-y-5 opacity-70 sm:gap-x-10">
                {[
                  ['shopify', 'Shopify'],
                  ['amazon', 'Amazon'],
                  ['tiktok', 'TikTok Shop'],
                  ['meta', 'Meta Ads'],
                  ['googleads', 'Google Ads'],
                  ['googleanalytics', 'Google Analytics'],
                  ['googlesheets', 'Google Sheets'],
                ].map(([file, name]) => (
                  <li key={file} className="flex items-center gap-2.5 text-base font-semibold text-slate-300">
                    <img src={`/logos/sources/${file}.svg`} alt="" width="24" height="24" className="h-6 w-6" />
                    <span>{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Problem Section - White */}
        <section className="bg-white py-20 md:py-24">
          <div className="mx-auto grid max-w-7xl gap-10 px-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
            <div className="max-w-xl">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#1a4e94]">Why it breaks</p>
              <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-gray-900 mb-8">Why the reports disagree</h2>
              <p className="text-xl text-gray-700 leading-relaxed md:text-2xl">
                Sales, ad, inventory, and finance data often disagree across Amazon, Shopify, and the ad platforms. Someone has to reconcile the definitions before the reports can be reviewed.
              </p>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.45)] md:p-10">
                <p className="mb-6 text-lg font-semibold text-gray-900">The systems that often need to agree</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <ul className="space-y-3 text-lg text-gray-700">
                    <li className="flex items-start gap-3">
                      <span className="text-[#1a4e94] font-semibold">•</span>
                      <span>Amazon Seller Central</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#1a4e94] font-semibold">•</span>
                      <span>Shopify stores</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#1a4e94] font-semibold">•</span>
                      <span>TikTok Shop</span>
                    </li>
                  </ul>
                  <ul className="space-y-3 text-lg text-gray-700">
                    <li className="flex items-start gap-3">
                      <span className="text-[#1a4e94] font-semibold">•</span>
                      <span>Google Ads, Meta Ads, Amazon Ads</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#1a4e94] font-semibold">•</span>
                      <span>QuickBooks or NetSuite</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#1a4e94] font-semibold">•</span>
                      <span>Inventory systems</span>
                    </li>
                  </ul>
                </div>
            </div>
          </div>
        </section>

        {/* Solution Section - Light Gray */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="mx-auto max-w-7xl px-6">
            <div className="max-w-4xl">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#1a4e94]">A practical architecture</p>
              <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-gray-900 mb-8">What the warehouse does</h2>
              <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-12">
                A warehouse stores source data in one place, then models it for sales, cost, ad, inventory, and profit reporting.
              </p>
            </div>

            <div>
              <h3 className="mb-8 text-2xl font-semibold text-gray-900">The stack</h3>
              <div className="grid gap-5 lg:grid-cols-3">
                <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.4)] md:p-8">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">01 / Integrate</p>
                  <h4 className="text-2xl font-semibold text-gray-900 mb-4">Data integration</h4>
                  <p className="text-lg text-gray-700 mb-4">Copies source data from Amazon, Shopify, and ad platforms into the warehouse.</p>
                  <div className="divide-y divide-slate-200 border-t border-slate-200">
                    <div className="py-4">
                      <p className="text-gray-900"><strong className="text-[#1a4e94]">Fivetran:</strong> Managed connectors. Check each required source, table, and region before choosing it.</p>
                    </div>
                    <div className="py-4">
                      <p className="text-gray-900"><strong className="text-[#1a4e94]">Airbyte:</strong> Connector software with self-hosted and managed options. Your team still owns setup, testing, and source changes.</p>
                    </div>
                    <div className="py-4">
                      <p className="text-gray-900"><strong className="text-[#1a4e94]">Daton:</strong> An ecommerce-focused connector option. Test it against the Amazon and Shopify tables your reports need.</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.4)] md:p-8">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">02 / Model</p>
                  <h4 className="text-2xl font-semibold text-gray-900 mb-4">Data warehouse</h4>
                  <p className="text-lg text-gray-700 mb-4">Stores source data and runs the SQL models behind the reports.</p>
                  <div className="divide-y divide-slate-200 border-t border-slate-200">
                    <div className="py-4">
                      <p className="text-gray-900"><strong className="text-[#1a4e94]">Snowflake:</strong> Separates storage and compute and connects to common ingestion and BI tools.</p>
                    </div>
                    <div className="py-4">
                      <p className="text-gray-900"><strong className="text-[#1a4e94]">BigQuery:</strong> Google Cloud's managed warehouse and a natural tool to test when the rest of the data stack is on Google Cloud.</p>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.4)] md:p-8">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">03 / Use</p>
                  <h4 className="text-2xl font-semibold text-gray-900 mb-4">Reporting and BI</h4>
                  <p className="text-lg text-gray-700 mb-4">Where people open dashboards, filter results, and investigate a number.</p>
                  <div className="divide-y divide-slate-200 border-t border-slate-200">
                    <div className="py-4">
                      <p className="text-gray-900"><strong className="text-[#1a4e94]">Tableau:</strong> Desktop authoring and published dashboards for visual analysis.</p>
                    </div>
                    <div className="py-4">
                      <p className="text-gray-900"><strong className="text-[#1a4e94]">Power BI:</strong> Reporting and semantic models in the Microsoft ecosystem.</p>
                    </div>
                    <div className="py-4">
                      <p className="text-gray-900"><strong className="text-[#1a4e94]">Looker:</strong> Browser-based BI with a modeled layer written in LookML.</p>
                    </div>
                    <div className="py-4">
                      <p className="text-gray-900"><strong className="text-[#1a4e94]">Metabase:</strong> Open-source BI with a browser interface for queries and dashboards.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Case study Section - Dark */}
        <section className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_#173f78_0,_#0a192f_46%,_#071525_100%)] py-24 text-white md:py-28">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />
          <div className="relative mx-auto max-w-7xl px-6 md:px-8">
            <p className="text-sm font-semibold text-blue-300 tracking-[0.2em] uppercase mb-4">Case study</p>
            <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] mb-8 max-w-4xl">Seven ecommerce domains in one dbt project</h2>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed max-w-4xl mb-12">
              We organized commerce, finance, inventory, refunds, fulfillment, marketing, and creator data in Snowflake and dbt for Tableau.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-7"><p className="text-3xl font-semibold text-blue-300 mb-2">7</p><p className="text-gray-300">operating domains modeled together</p></div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-7"><p className="text-3xl font-semibold text-blue-300 mb-2">120</p><p className="text-gray-300">models declared in the generated dbt manifest</p></div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-7"><p className="text-3xl font-semibold text-blue-300 mb-2">10,105</p><p className="text-gray-300">order-line records reconciled during one connector migration</p></div>
            </div>
            <Link to="/case-studies/dtc-brand-portfolio" className="inline-flex items-center gap-2 rounded-xl bg-white px-7 py-4 font-semibold text-gray-900 hover:bg-gray-100 transition">
              Read the case study
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </section>

        {/* Timeline Section - White */}
        <section className="bg-white py-20 md:py-24">
          <div className="mx-auto max-w-7xl px-6">
            <div className="max-w-4xl mb-12">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#1a4e94]">Build in order</p>
              <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-gray-900 mb-6">A first-build sequence</h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                Start with source access and one set of reports. Reconcile those numbers before adding more sources.
              </p>
            </div>

            <div className="grid gap-5 lg:grid-cols-3">
              <div className="rounded-3xl border border-blue-100 bg-[#f7faff] p-7 md:p-8">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">01 / Connect</p>
                <h3 className="text-2xl font-semibold mb-3 text-gray-900">Connect the sources</h3>
                <p className="text-lg text-gray-700">Connect the commerce and advertising sources needed for the first reports. Set up access and test each load.</p>
              </div>

              <div className="rounded-3xl border border-blue-100 bg-[#f7faff] p-7 md:p-8">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">02 / Reconcile</p>
                <h3 className="text-2xl font-semibold mb-3 text-gray-900">Reconcile and model</h3>
                <p className="text-lg text-gray-700">Build reconciliation reports, turn source data into reporting models, and compare warehouse totals with each platform.</p>
              </div>

              <div className="rounded-3xl border border-blue-100 bg-[#f7faff] p-7 md:p-8">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#1a4e94]">03 / Review</p>
                <h3 className="text-2xl font-semibold mb-3 text-gray-900">Build and review reports</h3>
                <p className="text-lg text-gray-700">Build the executive summary and channel reports, then revise them after review.</p>
              </div>
            </div>

            <div className="mt-8 rounded-3xl border border-slate-200 bg-slate-50 p-8 lg:flex lg:items-start lg:gap-12">
              <p className="text-gray-900 font-semibold text-xl mb-3">Start with fewer sources</p>
              <p className="max-w-3xl text-lg text-gray-700">
                Start with the systems needed for the first reports. Add another source only after those reports reconcile to platform totals.
              </p>
            </div>
          </div>
        </section>

        {/* Data sources Section - Light Gray */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="mx-auto max-w-7xl px-6">
            <div className="max-w-4xl mb-12">
              <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-gray-900 mb-6">Sources to plan for</h2>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              <div className="rounded-3xl bg-[#0a192f] p-8 text-white shadow-[0_20px_60px_-40px_rgba(10,25,47,0.8)]">
                <h3 className="mb-6 text-2xl font-semibold text-white">Start here</h3>
                <ul className="space-y-3 text-slate-200">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full"></span>
                    Shopify API
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full"></span>
                    Amazon Seller Central
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full"></span>
                    Amazon Ads API
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full"></span>
                    Google Ads
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full"></span>
                    Meta Ads
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full"></span>
                    QuickBooks or NetSuite
                  </li>
                </ul>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.4)]">
                <h3 className="text-2xl font-semibold text-[#1a4e94] mb-6">Add next</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full"></span>
                    Google Analytics
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full"></span>
                    Klaviyo
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full"></span>
                    ShipBob or another 3PL
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full"></span>
                    TikTok Ads
                  </li>
                </ul>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.4)]">
                <h3 className="text-2xl font-semibold text-[#1a4e94] mb-6">Check access early</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full mt-2"></span>
                    <span>TikTok Shop: confirm the required endpoints and account access</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-[#1a4e94] rounded-full mt-2"></span>
                    <span>Walmart Marketplace: confirm the required endpoints and account access</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Tool Comparison Section - White */}
        <section className="bg-slate-50 py-20 md:py-24">
          <div className="mx-auto max-w-7xl px-6">
            <div className="max-w-4xl mb-16">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#1a4e94]">Decision guides</p>
              <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-gray-900 mb-6">How to choose the tools</h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                Compare each option against your required sources, cloud, report types, and the team that will operate it.
              </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Snowflake vs BigQuery */}
              <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.4)] md:p-10">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-6">Snowflake or BigQuery for ecommerce data</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Both are cloud data warehouses. Compare them against your cloud, query patterns, security requirements, and operating skills.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-xl font-semibold text-[#1a4e94] mb-4">Snowflake</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">Runs on AWS, Azure, and Google Cloud. Storage and compute are configured separately.</p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3"><strong>Consider it when:</strong> Your data spans clouds or your team already runs Snowflake</p>
                                      </div>
                  <div>
                    <h4 className="text-xl font-semibold text-[#1a4e94] mb-4">BigQuery</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">A managed warehouse on Google Cloud. Google Cloud services can feed or query it without adding another cloud.</p>
                    <p className="text-sm text-gray-600 leading-relaxed mb-3"><strong>Consider it when:</strong> Your data and technical team already use Google Cloud</p>
                                      </div>
                </div>
              </div>

              {/* Tableau, Power BI, Looker, or Metabase */}
              <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.4)] md:p-10">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-6">Tableau, Power BI, Looker, or Metabase</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Compare how each tool handles modeling, authoring, sharing, permissions, and the reports your team already uses.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg font-semibold text-[#1a4e94] mb-2">Tableau</h4>
                      <p className="text-gray-700 leading-relaxed text-sm">Desktop authoring and published dashboards for visual analysis. Test it with one of your harder reports.</p>
                                          </div>
                    <div>
                      <h4 className="text-lg font-semibold text-[#1a4e94] mb-2">Power BI</h4>
                      <p className="text-gray-700 leading-relaxed text-sm">Reporting and semantic models in the Microsoft ecosystem. Start here if the team already works in Excel and Microsoft 365.</p>
                                          </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg font-semibold text-[#1a4e94] mb-2">Looker</h4>
                      <p className="text-gray-700 leading-relaxed text-sm">Browser-based BI with a modeled layer written in LookML. Check whether the team can own that model.</p>
                                          </div>
                    <div>
                      <h4 className="text-lg font-semibold text-[#1a4e94] mb-2">Metabase</h4>
                      <p className="text-gray-700 leading-relaxed text-sm">Open-source BI with a browser interface for queries and dashboards. Test permissions, sharing, and the queries your team needs.</p>
                                          </div>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-white border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-700 leading-relaxed"><strong>How to test:</strong> Rebuild one report that people already use. Compare the totals, authoring work, permissions, and publishing steps before choosing a tool.</p>
                </div>
              </div>

              {/* Fivetran vs Airbyte */}
              <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.4)] md:p-10">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-6">Fivetran or Airbyte for ecommerce</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Compare source coverage and decide who will run connector infrastructure, monitor loads, and respond when a source changes.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-xl font-semibold text-[#1a4e94] mb-4">Fivetran</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">Fivetran runs managed connectors. Your team still needs to test source coverage, monitor loads, and own downstream models.</p>
                                      </div>
                  <div>
                    <h4 className="text-xl font-semibold text-[#1a4e94] mb-4">Airbyte</h4>
                    <p className="text-gray-700 leading-relaxed mb-4">Airbyte offers self-hosted and managed options. With self-hosting, your team runs the infrastructure and tests connector updates.</p>
                                      </div>
                </div>
              </div>

              {/* Daton vs Fivetran */}
              <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.4)] md:p-10">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-6">Daton or Fivetran for Amazon sellers</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Start with the Amazon Seller Central tables, regions, and refresh timing you need, then test each connector against that list.
                </p>
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-gray-900 mb-2">Test Daton when:</p>
                    <ul className="space-y-2 text-gray-700 leading-relaxed">
                      <li className="flex items-start gap-2">
                        <span className="text-[#1a4e94] font-semibold mt-1">✓</span>
                        <span>Most required sources are Amazon Seller Central, Shopify, or ecommerce ad platforms</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#1a4e94] font-semibold mt-1">✓</span>
                        <span>Its connector covers the tables, regions, and refresh timing you need</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#1a4e94] font-semibold mt-1">✓</span>
                        <span>You don't need sources outside its ecommerce focus</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-2">Test Fivetran when:</p>
                    <ul className="space-y-2 text-gray-700 leading-relaxed">
                      <li className="flex items-start gap-2">
                        <span className="text-[#1a4e94] font-semibold mt-1">✓</span>
                        <span>You integrate data beyond ecommerce (Salesforce, NetSuite, custom databases)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#1a4e94] font-semibold mt-1">✓</span>
                        <span>You need managed connectors outside ecommerce</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#1a4e94] font-semibold mt-1">✓</span>
                        <span>Your team wants the vendor to run connector infrastructure</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Triple Whale vs Custom */}
              <div className="h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_18px_50px_-40px_rgba(15,23,42,0.4)] md:p-10">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-6">Triple Whale or a custom data warehouse</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Triple Whale provides packaged ecommerce reporting. A warehouse lets you combine source data and define your own models.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-xl font-semibold text-[#1a4e94] mb-4">Triple Whale</h4>
                    <p className="text-gray-700 leading-relaxed mb-3"><strong>Test it when:</strong> You want packaged Shopify reporting and its definitions cover the questions you need to answer.</p>
                    <p className="text-gray-700 leading-relaxed"><strong>Check first:</strong> Required connectors, metric definitions, exports, and the reports you need to change.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-[#1a4e94] mb-4">Custom warehouse</h4>
                    <p className="text-gray-700 leading-relaxed mb-3"><strong>Test it when:</strong> You need to combine brands or sources and define metrics in your own models.</p>
                    <p className="text-gray-700 leading-relaxed"><strong>Plan for:</strong> Implementation, reconciliation, monitoring, and ongoing model changes.</p>
                  </div>
                </div>
                <p className="mt-6 text-gray-700">
                  <Link to="/compare/triple-whale-vs-custom-data-warehouse" className="font-semibold text-[#1a4e94] underline underline-offset-4">Read the full Triple Whale comparison</Link>
                  {' '}or <Link to="/compare" className="font-semibold text-[#1a4e94] underline underline-offset-4">compare other ecommerce reporting tools</Link>.
                </p>
              </div>

              {/* Implementation Help */}
              <div className="rounded-3xl border border-blue-100 bg-[#f7faff] p-8 md:p-10 lg:col-span-2">
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-6">What to ask an implementation partner</h3>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  If your team doesn't have a data engineer, ask who will own connector setup, reconciliation, models, reports, and monitoring.
                </p>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-3">Connector source coverage</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Ask the partner to show coverage for the Amazon Seller Central and Shopify tables you need, plus how they reconcile orders, refunds, fees, and ad spend.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-3">Models and reconciliation</h4>
                    <p className="text-gray-700 leading-relaxed">
                      Connector setup is only the first step. Someone still has to define models, compare totals with each source, and build the reports.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-3">What to check before hiring</h4>
                    <ul className="space-y-2 text-gray-700 leading-relaxed">
                      <li className="flex items-start gap-2">
                        <span className="text-[#1a4e94] font-semibold mt-1">•</span>
                        <span>Work with the ecommerce sources you use</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#1a4e94] font-semibold mt-1">•</span>
                        <span>Examples of how they define attribution and ROAS</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#1a4e94] font-semibold mt-1">•</span>
                        <span>A source list, sample model, or case study you can inspect</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#1a4e94] font-semibold mt-1">•</span>
                        <span>A clear scope, source list, owner, and acceptance checks</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Resources Section - White */}
        <section className="bg-white py-20 md:py-24">
          <div className="mx-auto max-w-7xl px-6">
            <div className="max-w-4xl mb-12">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#1a4e94]">Keep reading</p>
              <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-gray-900">Guides</h2>
              <p className="text-xl text-gray-600 mt-4">
                Comparisons and setup guides for connectors, warehouses, and BI tools.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Link to="/blog/where-do-amazon-seller-fees-live-in-the-sp-api" className="group flex min-h-64 flex-col rounded-3xl border border-slate-200 p-7 transition-all hover:-translate-y-1 hover:border-[#1a4e94]/40 hover:shadow-xl md:p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#1a4e94]">Amazon seller fees and payout reconciliation</h3>
                <p className="text-gray-600 mb-4">Find the SP-API sources for payouts, SKU margin, FBA storage fees, and reimbursements.</p>
                <span className="mt-auto text-[#1a4e94] font-medium">Read guide →</span>
              </Link>

              <Link to="/blog/fivetran-vs-airbyte-vs-daton-ecommerce" className="group flex min-h-64 flex-col rounded-3xl border border-slate-200 p-7 transition-all hover:-translate-y-1 hover:border-[#1a4e94]/40 hover:shadow-xl md:p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#1a4e94]">Fivetran, Airbyte, and Daton</h3>
                <p className="text-gray-600 mb-4">Compare connector coverage, operating work, and warehouse load for Fivetran, Airbyte, and Daton.</p>
                <span className="mt-auto text-[#1a4e94] font-medium">Read guide →</span>
              </Link>

              <Link to="/blog/power-bi-vs-tableau-ecommerce" className="group flex min-h-64 flex-col rounded-3xl border border-slate-200 p-7 transition-all hover:-translate-y-1 hover:border-[#1a4e94]/40 hover:shadow-xl md:p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#1a4e94]">Power BI, Tableau, Looker, and Metabase</h3>
                <p className="text-gray-600 mb-4">Compare Tableau, Power BI, Looker, and Metabase for modeling, authoring, sharing, and Excel workflows.</p>
                <span className="mt-auto text-[#1a4e94] font-medium">Read guide →</span>
              </Link>

              <Link to="/blog/bigquery-vs-snowflake-ecommerce" className="group flex min-h-64 flex-col rounded-3xl border border-slate-200 p-7 transition-all hover:-translate-y-1 hover:border-[#1a4e94]/40 hover:shadow-xl md:p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-[#1a4e94]">BigQuery, Snowflake, and Azure</h3>
                <p className="text-gray-600 mb-4">Compare BigQuery, Snowflake, and Azure against cloud fit, query patterns, and operating work.</p>
                <span className="mt-auto text-[#1a4e94] font-medium">Read guide →</span>
              </Link>
            </div>

            <div className="mt-8 text-center">
              <Link to="/blog" className="text-[#1a4e94] hover:text-[#0f2d54] font-medium text-lg">
                View all articles →
              </Link>
            </div>
          </div>
        </section>

        {/* Interactive Quiz Section - Dark */}
        <section className="relative overflow-hidden bg-[radial-gradient(circle_at_top_left,_#173f78_0,_#0a192f_46%,_#071525_100%)] py-24 md:py-28">
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:40px_40px]" />

          <div className="relative max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-blue-300">Stack builder</p>
              <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-white mb-6">Choose a stack to test</h2>
              <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Answer a short set of questions about your sources, team, and current tools. The result is a stack to test, not a buying decision.
              </p>
            </div>

            <ToolQuiz />

            <p className="mt-10 text-center text-base leading-relaxed text-gray-300">
              Want one pick per layer, with the reasons? Try the{' '}
              <Link to="/tools/ecommerce-data-stack-quiz" className="font-semibold text-white underline underline-offset-4 hover:text-blue-200">ecommerce data stack quiz</Link>, or read the free{' '}
              <Link to="/white-papers/ecommerce-data-stack" className="font-semibold text-white underline underline-offset-4 hover:text-blue-200">ecommerce data stack white paper</Link>.
            </p>
          </div>
        </section>

        {/* FAQ Section - White */}
        <section className="bg-white py-20 md:py-24">
          <div className="mx-auto max-w-5xl px-6">
            <div className="max-w-4xl mb-12">
              <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#1a4e94]">FAQ</p>
              <h2 className="text-4xl md:text-5xl font-semibold leading-[1.1] text-gray-900">Common questions</h2>
            </div>

            <div className="space-y-4 max-w-4xl">
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-md">
                <button
                  onClick={() => setOpenFaq(openFaq === 0 ? null : 0)}
                  className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-gray-50 transition"
                >
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-900 pr-4">How long does it take to build?</h3>
                  <svg
                    className={`w-6 h-6 text-gray-600 transition-transform flex-shrink-0 ${openFaq === 0 ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === 0 && (
                  <div className="px-6 md:px-8 pb-6 md:pb-8 text-gray-700 text-lg leading-relaxed">
                    The sequence is source access, reconciliation and modeling, then report review. Timing depends on source access, data quality, metric definitions, and the number of reports.
                  </div>
                )}
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-md">
                <button
                  onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                  className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-gray-50 transition"
                >
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-900 pr-4">How much maintenance is there afterwards?</h3>
                  <svg
                    className={`w-6 h-6 text-gray-600 transition-transform flex-shrink-0 ${openFaq === 1 ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === 1 && (
                  <div className="px-6 md:px-8 pb-6 md:pb-8 text-gray-700 text-lg leading-relaxed">
                    APIs and schemas change, connectors fail, and business rules evolve. Assign someone to monitor loads, investigate mismatches, and update models and reports.
                  </div>
                )}
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-md">
                <button
                  onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                  className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-gray-50 transition"
                >
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-900 pr-4">Can I start small and add more later?</h3>
                  <svg
                    className={`w-6 h-6 text-gray-600 transition-transform flex-shrink-0 ${openFaq === 2 ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === 2 && (
                  <div className="px-6 md:px-8 pb-6 md:pb-8 text-gray-700 text-lg leading-relaxed">
                    Yes. Start with one or two reports tied to a current decision, then add sources and reports after the first set reconciles.
                  </div>
                )}
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white transition-shadow hover:shadow-md">
                <button
                  onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                  className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-gray-50 transition"
                >
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-900 pr-4">How do I know if my data is accurate?</h3>
                  <svg
                    className={`w-6 h-6 text-gray-600 transition-transform flex-shrink-0 ${openFaq === 3 ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaq === 3 && (
                  <div className="px-6 md:px-8 pb-6 md:pb-8 text-gray-700 text-lg leading-relaxed">
                    Build reconciliation reports that compare warehouse totals with each source. Document any accepted difference, such as time zones, refunds, cancellations, or platform-specific fees.
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-slate-50 px-6 py-20 md:py-28">
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-[radial-gradient(circle_at_top_left,_#173f78_0,_#0a192f_44%,_#071525_100%)] p-10 text-center text-white ring-1 ring-inset ring-white/10 md:p-16">
            <div className="absolute left-0 top-0 h-80 w-80 rounded-full bg-blue-400/10 blur-3xl" />
            <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:40px_40px]" />

            <div className="relative z-10">
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">Start with the mismatch</p>
              <h2 className="mb-6 text-4xl font-semibold leading-[1.1] tracking-tight text-white md:text-5xl">Bring the reports that disagree</h2>
              <p className="mx-auto mb-10 max-w-3xl text-xl leading-relaxed text-slate-300 md:text-2xl">
                Bring the Shopify, Amazon, ad, inventory, or finance reports that do not agree. We can trace the definitions and decide where to start.
              </p>
              <a
                href="mailto:linus@greyfielddata.com?subject=Ecommerce Data Warehouse Inquiry"
                className="group inline-flex items-center gap-2 rounded-xl bg-white px-10 py-5 text-lg font-semibold text-[#1a4e94] shadow-xl shadow-black/15 transition hover:bg-slate-100"
              >
                Review your reporting problem
                <span aria-hidden="true" className="transition-transform group-hover:translate-x-1">→</span>
              </a>
            </div>
          </div>
        </section>

        {/* About Section - Light Gray */}
        <section className="border-t border-slate-200 bg-white py-16">
          <div className="mx-auto max-w-5xl px-6">
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#1a4e94]">About Greyfield Data</p>
            <h3 className="mb-4 text-2xl font-semibold text-gray-900">Ecommerce data engineering and reporting</h3>
            <p className="text-gray-700 text-lg leading-relaxed">
              We build data warehouses and reports for ecommerce brands selling on Amazon, Shopify, and TikTok Shop. Client work includes multi-brand portfolios, DTC brands, and Amazon sellers.
            </p>
          </div>
        </section>

      </div>
    </>
  );
}
